import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { arrType, getColDefaultValue } from "data/globalData";
import RenderInputField from "components/EboStoreV4/CommonComponents/RenderInputFiled/RenderInputFiled";
import { SbtRoleAccess, storeBudgetTrackerFields } from "data/storeBudgetData";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

const formatCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;
const formatDate = (value) => (value ? String(value).split("T")[0] : "-");
const isTaggedRow = (row) => Boolean(row?.tagged);
const getTaggedProjectId = (row, fallback = "-") => row?.tagged;

const mapApiRowToBudgetRow = (row, index) => {
  const poValue = Number(row?.PO_WITHOUT_TAX) || 0;
  const poWithTax = Number(row?.PO_WITH_TAX) || 0;
  const grossAmount = Number(row?.GROSS_AMT) || 0;
  const netAmount = Number(row?.NET_AMT) || 0;

  return {
    id: `${row?.PO_NO || "PO"}-${row?.GRN_NO || "GRN"}-${row?.INVOICE_NO || "INV"}${index ? index : ""}`,
    material: row?.DEPARTMENT,
    vendor_name: row?.VENDOR_NAME,
    po_number: row?.PO_NO,
    // po_status: row?.STATUS,
    po_remark: row?.REMARK,
    tagged: row?.TAGGED ?? row?.tagged ?? row?.tagged_key ?? row?.taggedKey ?? row?.REMARK,
    project_id: row?.PROJECT_ID ?? row?.project_id ?? row?.PROJECTID ?? row?.PROJECTID_NO,
    po_date: formatDate(row?.PO_DATE),
    po_value: poValue,
    po_tax: Math.max(poWithTax - poValue, 0),
    // invoice_receive_date: formatDate(row?.INVOICE_DATE),
    invoice_no: row?.INVOICE_NO,
    invoice_date: formatDate(row?.INVOICE_DATE),
    // delay_in_invoice: "-",
    invoice_value_without_tax: grossAmount,
    invoice_value_with_tax: netAmount,
    // grn_request_date: "-",
    grn_date: formatDate(row?.GRNDATE),
    grn_no: row?.GRN_NO,
    // grn_tat: "-",
    store_name: row?.SITE_NAME,
    // total_budget: Number(row?.total_budget) || 0,
    site_code: row?.SITE_CODE,
    // remark: row?.REMARK,
    item_status: row?.GRN_STATUS,
    barcode: row?.BARCODE,
  };
};

const mapApiRows = (rows = []) => {
  const keyCount = new Map();
  return rows.map((row) => {
    const baseKey = `${row?.PO_NO || "PO"}-${row?.GRN_NO || "GRN"}-${row?.INVOICE_NO || "INV"}`;
    const count = keyCount.get(baseKey) || 0;
    keyCount.set(baseKey, count + 1);
    return mapApiRowToBudgetRow(row, count === 0 ? "" : `-${count}`);
  });
};

const getDataColumns = (projectTag) => [
  { field: "material", headerName: "Materials", minWidth: 220, flex: 1.3 },
  { field: "store_name", headerName: "Location (Store Name)", minWidth: 190, flex: 1.3 },
  { field: "vendor_name", headerName: "Vendor Name", minWidth: 170, flex: 1.2 },
  { field: "po_number", headerName: "PO No.", minWidth: 120, flex: 0.9 },
  { field: "po_date", headerName: "PO Date", minWidth: 120, flex: 0.9 },
  {
    field: "po_value",
    headerName: "PO amt without tax",
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => formatCurrency(row?.po_value),
  },
  {
    field: "po_amt_with_tax",
    headerName: "PO amt with Tax",
    minWidth: 145,
    flex: 1,
    renderCell: ({ row }) => formatCurrency((row?.po_value || 0) + (row?.po_tax || 0)),
  },
  // { field: "po_status", headerName: "PO STATUS", minWidth: 120, flex: 0.9 },
  // { field: "invoice_receive_date", headerName: "Invoice Recieve Date", minWidth: 165, flex: 1.1 },
  { field: "invoice_no", headerName: "Invoice No", minWidth: 125, flex: 0.9 },
  { field: "invoice_date", headerName: "Invoice date", minWidth: 125, flex: 0.9 },
  // { field: "delay_in_invoice", headerName: "Dealy in Invoice", minWidth: 130, flex: 0.9 },
  {
    field: "invoice_value_without_tax",
    headerName: "Invoice amt without tax",
    minWidth: 170,
    flex: 1.1,
    renderCell: ({ row }) => formatCurrency(row?.invoice_value_without_tax),
  },
  {
    field: "invoice_value_with_tax",
    headerName: "Invoice amt with Tax",
    minWidth: 160,
    flex: 1,
    renderCell: ({ row }) => formatCurrency(row?.invoice_value_with_tax),
  },
  // { field: "grn_request_date", headerName: "GRN Request Date", minWidth: 155, flex: 1 },
  { field: "grn_no", headerName: "GRN No", minWidth: 120, flex: 0.9 },
  { field: "grn_date", headerName: "GRN Date", minWidth: 120, flex: 0.9 },
  { field: "item_status", headerName: "Status", minWidth: 100, flex: 0.8 },
].map((column) => ({
  headerAlign: "center",
  align: "center",
  sortable: false,
  ...column,
}));

const comparisonCardSx = {
  flex: 1,
  minWidth: 180,
  p: 2,
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fafafa",
};

const invoicePanelSx = {
  flex: 1,
  minWidth: 320,
  borderRadius: 3,
  overflow: "hidden",
  border: "1px solid #dbe2ef",
  backgroundColor: "#ffffff",
};

const invoicePanelHeaderSx = (backgroundColor) => ({
  px: 2,
  py: 1.5,
  backgroundColor,
  color: "#fff",
});

const invoiceRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  px: 2,
  py: 1.5,
  borderBottom: "1px solid #eef2f7",
};

const initial_state = () => {
    const obj = {};

    storeBudgetTrackerFields?.forEach((attr) => {
      obj[attr.key] = getColDefaultValue(attr);
    });
    return obj;
  };

export default function CreateStoreBudget() {
  const location = useLocation();
  const storeBudgetData = location.state?.data;
  const isSubmitted = storeBudgetData?.status === "submitted";
  const [storeBudget, setStoreBudget] = useState(storeBudgetData === undefined ? initial_state() : storeBudgetData);
  const [poNumberFilter, setPoNumberFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [storeData, setStoreData] = useState([]);
  const [poData, setPoData] = useState([]);
  const [isSelectedPoDialogOpen, setIsSelectedPoDialogOpen] = useState(false);
  const [sortedFilteredRows, setSortedFilteredRows] = useState([]);
  const [savedRows, setSavedRows] = useState([]);
  const [isPoTableLoading, setIsPoTableLoading] = useState(false);

  const { user } = AuthState();
  const roleData = SbtRoleAccess(user)
  const isViewOnly = roleData?.roles === "VIEWER"

  const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
  const navigate = useNavigate();

  const handleBackClick = () => {

			navigate(`/explore/store_budget_tracker/store_budgets`)
    	}
  

  const getStores = async () => {
    try {
      setLoadingOpen(true);
      await pythonVmInstance({
        url: `/store-budget-tracker/get-store`,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      }).then((res) => {
        setStoreData(res?.data);
        setLoadingOpen(false);
      });
    } catch (error) {
      setSnackBarOpen(true);
      setLoadingOpen(false);
      setSnackBar({
        ...snackBar,
        message: "Failed to download file",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    }
  };
  const getPO = async (poFilter = "") => {
    try {
      setIsPoTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-po`,
        params: {
          store: storeBudget?.store_name,
          id : storeBudget?.id,
          ...(poFilter ? { po_number: poFilter } : {}),
        },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      setPoData((prev) => {
        const existingKeys = new Set(prev.map((r) => `${r?.PO_NO}-${r?.GRN_NO}-${r?.INVOICE_NO}`));
        const newRows = (res?.data || []).filter((r) => !existingKeys.has(`${r?.PO_NO}-${r?.GRN_NO}-${r?.INVOICE_NO}`));
        return [...prev, ...newRows];
      });
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: "Failed to download file", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
    } finally {
      setIsPoTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const getSelectedPO = async () => {
    try {
      setIsPoTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-selected-po`,
        params: { id: storeBudgetData?.id },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      const data = res?.data || [];
      setSavedRows(data);
      return [...new Set(data.map((r) => r?.po_number || r?.PO_NO).filter(Boolean))].join(",");
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: "Failed to download file", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return "";
    } finally {
      setIsPoTableLoading(false);
      setLoadingOpen(false);
    }
  };
  const handleCreateStoreBudget = async (is_submitted) => {
    try {
      setLoadingOpen(true);
      const data = {
          ...storeBudget,
          po_details: selectedPoRows,
          email: user.email,
          is_submitted: is_submitted || false,
        }
      await pythonVmInstance({
        url: `/store-budget-tracker/create`,
        data: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      }).then((res) => {
        setLoadingOpen(false);
        setSnackBarOpen(true);
        setSnackBar({
          ...snackBar,
          message: storeBudgetData ? "Updated store budget successfully" : "created store budget successfully",
          severity: "success",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
        handleBackClick()
      });
    } catch (error) {
      setSnackBarOpen(true);
      setLoadingOpen(false);
      setSnackBar({
        ...snackBar,
        message: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    }
  };

  useEffect(() => {
    getStores();
  }, []);
  useEffect(() => {
    if (isSubmitted) return;
    if (storeBudget?.store_name != "SELECT" && storeBudget?.store_name != null) {
      if (storeBudgetData?.id) {
        getSelectedPO().then((savedPoNums) => {
          getPO(savedPoNums || "");
        });
      } else {
        getPO();
      }
    } else {
      setPoData([]);
    }
  }, [storeBudget?.store_name]);

  useEffect(() => {
    if (isSubmitted && storeBudgetData?.id) {
      getSelectedPO();
    }
  }, []);

  useEffect(() => {
    const carpetArea = Number(storeBudget?.carpet_area) || 0;
    const stdPerSqFeet = Number(storeBudget?.std_per_sq_feet) || 0;
    const calculatedTotalBudget = carpetArea * stdPerSqFeet;

    setStoreBudget((prev) => {
      if ((Number(prev?.total_budget) || 0) === calculatedTotalBudget) {
        return prev;
      }

      return {
        ...prev,
        total_budget: calculatedTotalBudget,
      };
    });
  }, [storeBudget?.carpet_area, storeBudget?.std_per_sq_feet]);

  useEffect(() => {
    setStoreBudget((prev) => {
      const selectedStore = storeData?.find((store) => store.store_name === storeBudget?.store_name);

      return {
        ...prev,
        project: selectedStore?.project || "",
        city: selectedStore?.city || "",
        state: selectedStore?.state || "",
        store_code: selectedStore?.site_code || "",
      };
    });
  }, [storeBudget?.store_name, storeBudget?.channel, storeData]);

  const poRows = useMemo(() => {
    if (isSubmitted) {
      return savedRows.map((row, i) => ({ ...row, id: row.id || `${row.po_number}-${row.grn_no}-${row.invoice_no}-${i}` }));
    }
    return mapApiRows(poData);
  }, [poData, savedRows, isSubmitted]);

  const selectableRowIds = useMemo(
    () => new Set(poRows.filter((row) => !isTaggedRow(row)).map((row) => row.id)),
    [poRows]
  );

  useEffect(() => {
    if (!savedRows.length || !poRows.length) return;
    if (isSubmitted) {
      setSelectedRows(poRows.map((row) => row.id));
      return;
    }
    const savedKeyCount = new Map();
    savedRows.forEach((r) => {
      const key = `${r?.BARCODE || r?.barcode}|${r?.PO_NO || r?.po_number}|${r?.GRN_NO || r?.grn_no}|${r?.INVOICE_NO || r?.invoice_no}`;
      savedKeyCount.set(key, (savedKeyCount.get(key) || 0) + 1);
    });
    const matchedKeyCount = new Map();
    const matchedIds = [];
    poRows.forEach((row) => {
      const key = `${row?.barcode}|${row?.po_number}|${row?.grn_no}|${row?.invoice_no}`;
      if (!savedKeyCount.has(key)) return;
      const matched = matchedKeyCount.get(key) || 0;
      if (matched < savedKeyCount.get(key)) {
        matchedIds.push(row.id);
        matchedKeyCount.set(key, matched + 1);
      }
    });
    setSelectedRows(matchedIds.filter((id) => selectableRowIds.has(id)));
  }, [poRows, savedRows, selectableRowIds]);

  const selectedPoRows = useMemo(
    () => poRows.filter((row) => selectedRows.includes(row.id)),
    [poRows, selectedRows]
  );

  const filteredRows = useMemo(() => {
    const poFilters = poNumberFilter.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    const normalizedStatusFilter = statusFilter.trim().toLowerCase();
    return poRows.filter((row) => {
      const matchesPoNumber = !poFilters.length || poFilters.some((f) => row.po_number?.toLowerCase().includes(f));
      const matchesStatus = row.item_status?.toLowerCase().includes(normalizedStatusFilter);
      return matchesPoNumber && matchesStatus;
    });
  }, [poNumberFilter, poRows, statusFilter]);

  const gridRows = useMemo(
    () => (isSubmitted ? filteredRows : sortedFilteredRows),
    [filteredRows, sortedFilteredRows, isSubmitted]
  );

  const visibleSelectableRowIds = useMemo(
    () => gridRows.filter((row) => !isTaggedRow(row)).map((row) => row.id),
    [gridRows]
  );

 

  useEffect(() => {
    const selectedSet = new Set(selectedRows);
    setSortedFilteredRows(
      [...filteredRows].sort((a, b) => (selectedSet.has(a.id) ? 0 : 1) - (selectedSet.has(b.id) ? 0 : 1))
    );
  }, [filteredRows, selectedRows]);

  const selectedPoNumbers = useMemo(() => Array.from(new Set(selectedPoRows.map((row) => row.po_number).filter(Boolean))), [selectedPoRows]);
  const distinctSelectedPoRows = useMemo(() => {
    const poMap = new Map();

    selectedPoRows.forEach((row) => {
      const poNumber = row?.po_number || row?.id;
      const poValue = Number(row?.po_value) || 0;
      const poTax = Number(row?.po_tax) || 0;
      const poAmountWithTax = poValue + poTax;
      const invoiceValueWithTax = Number(row?.invoice_value_with_tax) || 0;
      const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();

      if (!poMap.has(poNumber)) {
        poMap.set(poNumber, {
          po_number: poNumber,
          po_value: poValue,
          po_tax: poTax,
          poAmountWithTax,
          po_status: row?.po_status || "-",
          canceled_amount: 0,
          completed_amount: 0,
        });
      }

      const existingPo = poMap.get(poNumber);

      if (normalizedGrnStatus.includes("cancel")) {
        existingPo.canceled_amount += poAmountWithTax;
      }

      if (normalizedGrnStatus.includes("complete")) {
        existingPo.completed_amount += invoiceValueWithTax;
      }
    });

    return Array.from(poMap.values());
  }, [selectedPoRows]);
  const isBudgetAvailable = Number(storeBudget?.total_budget) > 0;

  useEffect(() => {
    if (isSubmitted) return;
    if (!isBudgetAvailable && selectedRows.length) {
      setSelectedRows([]);
    }
  }, [isBudgetAvailable, selectedRows.length]);

  const budgetSummary = useMemo(() => {
    const totalBudget = Number(storeBudget?.total_budget) || 0;
    const poValue = distinctSelectedPoRows.reduce((sum, row) => sum + (row.po_value || 0), 0);
    const poTax = distinctSelectedPoRows.reduce((sum, row) => sum + (row.po_tax || 0), 0);
    const canceledAmount = distinctSelectedPoRows.reduce((sum, row) => sum + (row.canceled_amount || 0), 0);
    const completedAmount = distinctSelectedPoRows.reduce((sum, row) => sum + (row.completed_amount || 0), 0);
    const pendingAmount = distinctSelectedPoRows.reduce(
      (sum, row) => sum + Math.max((row.poAmountWithTax || 0) - (row.canceled_amount || 0) - (row.completed_amount || 0), 0),
      0
    );
    const overallPoAmount = poValue + poTax - canceledAmount;
    const grnValue = selectedPoRows.reduce((sum, row) => sum + (row.invoice_value_without_tax || 0), 0);
    const overallGrnAmount = selectedPoRows.reduce((sum, row) => sum + (row.invoice_value_with_tax || 0), 0);
    const grnTax = overallGrnAmount - grnValue;
    const budgetDifference = totalBudget - overallPoAmount;
    const utilization = totalBudget ? ((overallPoAmount / totalBudget) * 100).toFixed(2) : "0.00";

    return {
      totalBudget,
      poValue,
      poTax,
      overallPoAmount,
      canceledAmount,
      completedAmount,
      pendingAmount,
      grnValue,
      grnTax,
      overallGrnAmount,
      budgetDifference,
      utilization,
    };
  }, [distinctSelectedPoRows, storeBudget?.total_budget, selectedPoRows]);

  const handleRemovePoNumber = (poNumber) => {
    const rowIdsToRemove = new Set(
      poRows.filter((row) => row.po_number === poNumber).map((row) => row.id)
    );
    setSelectedRows((prev) => prev.filter((id) => !rowIdsToRemove.has(id)));
  };

  const validateStoreBudget = () => {
    const invalidValues = new Set(["", "SELECT", null, undefined]);
    const missingFields = storeBudgetTrackerFields
      .filter((field) => invalidValues.has(storeBudget[field.key]))
      .map((field) => field.label || field.key);
    return missingFields;
  };

  const handleSave = () => {
    const missingFields = validateStoreBudget();
    if (missingFields.length) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: `Please fill: ${missingFields.join(", ")}`, severity: "warning", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return;
    }
    handleCreateStoreBudget(false);
  };

  const handleSubmit = () => {
    const missingFields = validateStoreBudget();
    if (missingFields.length) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: `Please fill: ${missingFields.join(", ")}`, severity: "warning", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return;
    }

    handleCreateStoreBudget(true)

  };

  const selectedStore = storeData
    ?.filter((store) => store.channel === storeBudget?.channel)
    ?.map((st) => ({
      name: st.store_name,
      key: st.store_name,
    }));

  const projectTag = storeBudget?.project || storeBudgetData?.project || storeBudgetData?.id || "-";
  const tableColumns = useMemo(() => {
    if (isSubmitted) {
      return getDataColumns(projectTag);
    }

    const selectionColumn = {
      field: "__selection__",
      headerName: "",
      width: 64,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const checked = visibleSelectableRowIds.length > 0 && visibleSelectableRowIds.every((id) => selectedRows.includes(id));
        const indeterminate = visibleSelectableRowIds.some((id) => selectedRows.includes(id)) && !checked;

        return (
          <Checkbox
            size="small"
            checked={checked}
            indeterminate={indeterminate}
            disabled={isSubmitted || !isBudgetAvailable || !visibleSelectableRowIds.length || isViewOnly}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              setSelectedRows((prev) => {
                const next = new Set(prev);
                if (event.target.checked) {
                  visibleSelectableRowIds.forEach((id) => next.add(id));
                } else {
                  visibleSelectableRowIds.forEach((id) => next.delete(id));
                }
                return Array.from(next);
              });
            }}
          />
        );
      },
      renderCell: ({ row }) => {
        if (isTaggedRow(row)) {
          return (
            <Tooltip title={`Tagged with project id: ${getTaggedProjectId(row, projectTag)}`} arrow placement="top">
              <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", color: "#2563eb" }}>
                <LocalOfferOutlinedIcon fontSize="small" />
              </Box>
            </Tooltip>
          );
        }

        const checked = selectedRows.includes(row.id);
        const disabled = isSubmitted || !isBudgetAvailable || isViewOnly;

        return (
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              setSelectedRows((prev) => {
                const next = new Set(prev);
                if (event.target.checked) {
                  next.add(row.id);
                } else {
                  next.delete(row.id);
                }
                return Array.from(next);
              });
            }}
          />
        );
      },
    };

    return [selectionColumn, ...getDataColumns(projectTag)];
  }, [isBudgetAvailable, isSubmitted, isViewOnly, projectTag, selectedRows, visibleSelectableRowIds]);

  return (
    <Box padding={2}>
      <Stack padding={2} boxShadow={"0px 1px 3px rgba(0, 0, 0, 0.2)"} borderRadius={2} marginTop={2}>
        <Stack direction="row" alignItems="center" marginBottom={2} flexWrap="wrap" gap={1}>
          {storeBudgetTrackerFields?.map((row) => {
            let option_data = row.option_data ? row.option_data : {};
            let alreadyCreated = false;
            if (row?.key === "store_name") {
              option_data = {
                type: arrType,
                list: selectedStore,
              };
            }

            if (["channel","store_name","work_type"].includes(row?.key)){
                alreadyCreated = storeBudgetData && true
            }
            return (
              <RenderInputField
                key={row.key}
                column={{ ...row, option_data }}
                data={storeBudget}
                setData={setStoreBudget}
                width="24%"
                selectOptObjName={"option_data"}
                disabled={isSubmitted || isViewOnly || alreadyCreated}
              />
            );
          })}
        </Stack>
      </Stack>

      <Stack padding={2} boxShadow={"0px 1px 3px rgba(0, 0, 0, 0.2)"} borderRadius={2} marginTop={3} spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h6">Store Budget Allocation</Typography>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <TextField
              size="small"
              label="Search PO Number"
              placeholder="Enter PO Number"
              value={poNumberFilter}
              onChange={(event) => setPoNumberFilter(event.target.value)}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={!poNumberFilter.trim() || isSubmitted || isViewOnly}
              onClick={() => getPO(poNumberFilter)}
            >
              Search
            </Button>
            <TextField
              size="small"
              label="Search Status"
              placeholder="Enter Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            />
            <Chip label={`${selectedPoRows.length} Records`} color="primary" variant="outlined" />
            {/* {!isBudgetAvailable && (
              <Chip label="Enter Total Budget to enable selection" color="warning" variant="outlined" />
            )} */}
            {/* <Chip label={`${selectedPoNumbers.length} Selected`} color="success" variant="outlined" /> */}
            <Button variant="outlined" size="small" disabled={!selectedPoNumbers.length} onClick={() => setSelectedRows([])}>
              Clear Selection
            </Button>
          </Stack>
        </Stack>

        {!!selectedPoNumbers.length && (
          <Box sx={{ p: 1.75, borderRadius: 2, border: "1px solid #d1fae5", backgroundColor: "#f0fdf4" }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} marginBottom={1.5}>
        <Box sx={{ width: "100%"}}>
                <Typography variant="body2" color="text.secondary">Selected POs</Typography>
                <Typography variant="subtitle2" fontWeight={700}>{selectedPoNumbers.length} Selected</Typography>
              </Box>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {selectedPoNumbers.slice(0, 4).map((poNumber) => (
                <Box
                  key={poNumber}
                  sx={{
                    height: "100%",
                    minWidth: 150,
                    maxWidth: 210,
                    p: 1,
                    borderRadius: 2,
                    border: "1px solid #bbf7d0",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={400} sx={{ wordBreak: "break-word", flex: 1 }}>
                    {poNumber}
                  </Typography>
                  {(!isSubmitted && !isViewOnly) && <Box
                    onClick={() => handleRemovePoNumber(poNumber)}
                    disabled={isSubmitted || isViewOnly}
                    sx={{ cursor: "pointer", color: "#dc2626", fontWeight: 700, fontSize: 16, lineHeight: 1, px: 0.5 }}
                  >
                    ×
                  </Box>}
                </Box>
              ))}
              {selectedPoNumbers.length > 4 && (
                <Box
                  onClick={() => setIsSelectedPoDialogOpen(true)}
                  sx={{
                    minWidth: 120,
                    p: 1,
                    borderRadius: 2,
                    border: "1px dashed #86efac",
                    backgroundColor: "#dcfce7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={400} sx={{ color: "#166534" }}>
                    +{selectedPoNumbers.length - 4} more
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={isSubmitted ? filteredRows : sortedFilteredRows}
            columns={tableColumns}
            loading={isPoTableLoading}
            checkboxSelection={false}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 20, 50, 100]}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  __check__: false,
                },
              },
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
            }}
            sx={{
              height: "100%",
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#dbeafe",
                fontWeight: 600,
                borderRight: "1px solid #bfdbfe",
              },
              "& .MuiDataGrid-cell": {
                borderColor: "#eceff1",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #eceff1",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowY: "auto",
                minHeight: 320,
                maxHeight: 450
              },
            }}
          />
        </Box>

        <Dialog open={isSelectedPoDialogOpen} onClose={() => setIsSelectedPoDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Selected POs</DialogTitle>
          <DialogContent dividers>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {selectedPoNumbers.map((poNumber) => (
                <Box
                  key={poNumber}
                  sx={{
                    minWidth: 150,
                    maxWidth: 220,
                    p: 1,
                    borderRadius: 2,
                    border: "1px solid #bbf7d0",
                    backgroundColor: "#f0fdf4",
                    boxShadow: "0 6px 18px rgba(22, 101, 52, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={500} sx={{ wordBreak: "break-word", flex: 1 }}>
                    {poNumber}
                  </Typography>
                  {(!isSubmitted && !isViewOnly) && <Box
                    onClick={() => handleRemovePoNumber(poNumber)}
                    disabled={isSubmitted || isViewOnly}
                    sx={{ cursor: "pointer", color: "#dc2626", fontWeight: 700, fontSize: 16, lineHeight: 1, px: 0.5 }}
                  >
                    ×
                  </Box>}
                </Box>
              ))}
            </Stack>
          </DialogContent>
        </Dialog>

        {!!selectedPoNumbers.length && (
          <Box sx={{ p: 2, borderRadius: 2, background: "linear-gradient(180deg, #f8fbff 0%, #f8fafc 100%)", border: "1px solid #e2e8f0" }}>
            <Typography variant="h6" marginBottom={0.5}>
              PO vs GRN Snapshot
            </Typography>
            <Typography variant="body2" color="text.secondary" marginBottom={2}>
              Summary of selected POs, comparing PO amounts with received GRN values to provide a quick overview of budget utilization and pending amounts.
            </Typography>

            <Stack direction="row" gap={2} flexWrap="wrap">
              <Box sx={invoicePanelSx}>
                <Box sx={invoicePanelHeaderSx("#596fad")}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    PO Details
                  </Typography>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">Base Amount</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.poValue)}</Typography>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.poTax)}</Typography>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">Canceled Amount (incl tax)</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.canceledAmount)}</Typography>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">Pending Amount (incl tax)</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.pendingAmount)}</Typography>
                </Box>
                <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#eff6ff" }}>
                  <Typography variant="subtitle2" fontWeight={700}>PO Grand Total</Typography>
                  <Typography variant="h6" fontWeight={800} color="primary.main">
                    {formatCurrency(budgetSummary.overallPoAmount)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{...invoicePanelSx, height: "max-content"}}>
                <Box sx={invoicePanelHeaderSx("#299289")}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    GRN Details
                  </Typography>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">Base Amount</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.grnValue)}</Typography>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.grnTax)}</Typography>
                </Box>
                <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#ecfdf5" }}>
                  <Typography variant="subtitle2" fontWeight={700}>GRN Grand Total</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: "#047857" }}>
                    {formatCurrency(budgetSummary.overallGrnAmount)}
                  </Typography>
                </Box>
              </Box>
            </Stack>

            <Stack direction="row" gap={2} flexWrap="wrap" marginTop={2}>
              <Box sx={comparisonCardSx}>
                <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                <Typography variant="h6">{formatCurrency(budgetSummary.totalBudget)}</Typography>
              </Box>
              <Box sx={comparisonCardSx}>
                <Typography variant="body2" color="text.secondary">
                  {budgetSummary.budgetDifference >= 0 ? "Budget Remaining" : "Budget Exceeded"}
                </Typography>
                <Typography variant="h6" color={budgetSummary.budgetDifference >= 0 ? "success.main" : "error.main"}>
                  {formatCurrency(Math.abs(budgetSummary.budgetDifference))}
                </Typography>
              </Box>
              <Box sx={comparisonCardSx}>
                <Typography variant="body2" color="text.secondary">Budget Utilization</Typography>
                <Typography variant="h6">{budgetSummary.utilization}%</Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {(!isSubmitted && !isViewOnly) && (
          <Stack direction="row" justifyContent="space-between" gap={2} flexWrap="wrap" marginTop={1}>
            <Button variant="contained" size="large" onClick={handleSubmit} disabled={roleData?.roles === "EDITOR"}>
              Submit
            </Button>
            <Button variant="outlined" size="large" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
